import { FormEvent } from "react";
import { Box, Flex, FormControl, FormLabel, Text, FormControlProps } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { ITagsData, ITags, IKeyFigureGroup, IKeyFiguresData } from "src/api/server/extended-search";
import { Strong } from "src/components/base";
import { Button } from "src/components/base/button";
import { Switch } from "src/components/common/switch/switch";
import { Container } from "src/components/layout/container";
import { InvestableInCountrySwitch } from "src/components/pages/search/components/investable-in-country-switch";
import { KeyFiguresPopover } from "src/components/pages/search/components/key-figures-filter/key-figures-popover";
import { PopularFiltersPopover } from "src/components/pages/search/components/popular-filters/popular-filters-popover";
import { TagsPopover } from "src/components/pages/search/components/tags-filter/tags-popover";
import { ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";
import { useSearchStore } from "src/components/pages/search/store/search-store";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { EnumCountryKey } from "src/types/common-enum";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { SearchFilterHeader } from "../search-filter-header";

const SEARCH_FILTER_CONTAINER_TEST_ID = "search-filter-container";

interface ISearchFilterProps {
    resultCount: number | null;
    popularKeyFiguresGroup: IKeyFigureGroup;
    popularTags: ITags;
    keyFiguresData: IKeyFiguresData;
    tagsData: ITagsData;
    onSearchTermClear: () => void;
    onChangeInvestable: (event: FormEvent<HTMLInputElement>) => void;
    onReset: () => void;
    onSearch: (params: ISearchRequestParamsOmitCountry) => void;
    searchTerm?: string | string[];
}

const investableSwitchStyles: FormControlProps = {
    fontSize: "sm",
};

export const SearchFilterContainer = ({
    searchTerm,
    resultCount,
    popularKeyFiguresGroup,
    popularTags,
    keyFiguresData,
    tagsData,
    onSearchTermClear,
    onChangeInvestable,
    onReset,
    onSearch,
}: ISearchFilterProps) => {
    const { country } = useRouterLocale();
    const { t } = useTranslation(["extended-search", "common"]);
    const { formatNumber } = useNumberFormatter();
    const { isInvestable } = useSearchStore();

    const isInvestableFilterVisible = country !== EnumCountryKey.INT;
    const hasResults = resultCount !== null;
    const displayedResultsCount = hasResults ? formatNumber(resultCount) : "-";
    const wikifoliosFound = resultCount === 1 ? t("wikifolio-found") : t("wikifolios-found");

    return (
        <Box bg="gray.50" display={["none", "block"]} data-test-id={SEARCH_FILTER_CONTAINER_TEST_ID}>
            <Container pt="7" pb="5">
                <SearchFilterHeader searchTerm={searchTerm} handleOnClear={onSearchTermClear} />
                <Box display="flex" justifyContent="space-between" mb="2">
                    <Text alignSelf="center" fontSize="sm">
                        <Strong fontSize="xl">{displayedResultsCount}</Strong> {wikifoliosFound}
                    </Text>
                    <Button variant="ghost-underline" size="sm" onClick={onReset} px="1">
                        {t("buttons.reset-search-criteria")}
                    </Button>
                </Box>
                <Box>
                    <Flex gap="2" mb="3" w="100%">
                        <PopularFiltersPopover popularKeyFiguresGroup={popularKeyFiguresGroup} tagsData={popularTags} onSearch={onSearch} />
                        <KeyFiguresPopover keyFiguresData={keyFiguresData} onSearch={onSearch} />
                        <TagsPopover tagsData={tagsData} onSearch={onSearch} />
                    </Flex>
                    {isInvestableFilterVisible && (
                        <Flex flexDirection="row" gap={5}>
                            <FormControl display="inline-flex" alignItems="center" width="auto">
                                <Switch size="md" isChecked={isInvestable} onChange={onChangeInvestable} />
                                <FormLabel mb="0" ml={1} fontWeight="normal" fontSize="sm">
                                    {t("labels.investable-wikifolios-only", { ns: "common" })}
                                </FormLabel>
                            </FormControl>
                            <InvestableInCountrySwitch size="md" styles={investableSwitchStyles} onSearch={onSearch} />
                        </Flex>
                    )}
                </Box>
            </Container>
        </Box>
    );
};
